import { usePageAttributes } from '@/components/contexts/PageAttributesContext';
import { useSearchDirectoryData } from '@/context/searchDirectory/SearchDirectoryDataContext';
import { narrowSegmentationStats } from '@/helpers/narrowSegmentationStats';

interface Product {
  '@context': string;
  '@type': string;
  name: string;
  brand: {
    '@type': string;
    name: string;
  };
  aggregateRating?: {
    '@type': string;
    ratingValue: string;
    reviewCount: string;
    bestRating: string;
    worstRating: string;
  };
}

export const ProductSchema = () => {
  const { seoSegmentationStats } = useSearchDirectoryData();
  const { segmentNamePluralLowerCase } = usePageAttributes();

  const segmentationStats = narrowSegmentationStats(seoSegmentationStats);
  const totalReviewCount = segmentationStats?.totalReviewCount ?? 0;
  const avgReviewRating = segmentationStats?.avgReviewRating ?? 0;
  const showProductSchema = totalReviewCount >= 5 && avgReviewRating > 0;

  const nameTitle =
    segmentNamePluralLowerCase.charAt(0).toUpperCase() + segmentNamePluralLowerCase.slice(1);

  const ProductSchemaList = () => {
    const roundAvgReviewRating = avgReviewRating.toFixed(1);

    const product: Product = {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: nameTitle,
      brand: {
        '@type': 'Organization',
        name: 'Care.com',
      },
    };

    // Only add aggregateRating if rating is 4.5 or higher
    if (roundAvgReviewRating !== null && parseFloat(roundAvgReviewRating) >= 4.5) {
      product.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: roundAvgReviewRating,
        reviewCount: `${totalReviewCount}`,
        bestRating: '5',
        worstRating: '1',
      };
    }

    return product;
  };

  if (!showProductSchema) {
    return null;
  }

  return (
    <script
      data-testid="productSchema"
      type="application/ld+json"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: JSON.stringify(ProductSchemaList()) }}
    />
  );
};
